import { Session } from 'next-auth';

import { getAppConfig } from '@services/config';
import { CACHE_TAGS } from '@services/http/http';

import { sapicRepository } from '@repositories/sapic';

import { RootCategoryType } from '@app-types/api/categories';
import { SapicCategoryId, SapicFundingMode } from '@app-types/api/sapic';
import { CategoryCodeInfo, CategoryListType, MobilePlanType, OffersByCategoryCode } from '@app-types/plan';
import { SimulatorCart } from '@app-types/simulator/virtual-cart';

import { orderPlansByCategorys } from '@helpers/offer-wall';
import { mapApiPlanToMobilePlanType } from '@helpers/product';

import { API_X_VERSION } from '@constants/api';

import { apiHttpService } from './http';
import { createVirtualCart } from './simulator';

const appConfig = getAppConfig();

export async function getOfferWallData(
    categoriesCodes: string[],
    virtualCart: SimulatorCart = createVirtualCart(),
    session: Session | null = null,
    apiVersion: API_X_VERSION = API_X_VERSION.V5,
): Promise<CategoryListType> {
    try {
        const [sapiCategoryList, apiResponse] = await Promise.all([
            getApiProductDetails(categoriesCodes),
            sapicRepository.getMobilePlans(
                virtualCart,
                {
                    withDetails: true,
                    categories: [SapicCategoryId.SIMO, SapicCategoryId.PREMIUM],
                    fundingMode: SapicFundingMode.MIN_ONE_OFF,
                    limit: -1,
                },
                session || undefined,
                apiVersion,
            ),
        ]);

        const categoryList = sapiCategoryList.categories.map((category) => {
            const productsList: MobilePlanType[] = [];

            category.products.forEach((plan) => {
                const planSapic = apiResponse.products?.find((p) => p.gencode === plan.gencode);
                if (planSapic) {
                    productsList.push(mapApiPlanToMobilePlanType(plan, planSapic));
                }
            });

            return {
                id: category.id,
                name: category.name,
                description: category.description,
                code: category.code,
                subtitle: category.subtitle,
                url: category.url,
                products: productsList,
                _links: {
                    detailCategory: {
                        href: category._links.detailCategory.href,
                    },
                },
            };
        });

        return orderPlansByCategorys(categoryList);
    } catch (error) {
        console.error('Could not fetch product details', error);
        throw new Error(`Could not fetch product details`);
    }
}

export async function getApiProductDetails(
    url: string[],
    withProducts: boolean = true,
    withDetails: boolean = true,
): Promise<RootCategoryType> {
    return apiHttpService.get<RootCategoryType>(
        `${appConfig.sapi.url}/ventes/categories`,
        {
            codes: url.join(','),
            voirProduits: `${withProducts}`,
            avecDetails: `${withDetails}`,
        },
        {
            tags: [CACHE_TAGS.FAI],
            ttl: parseInt(appConfig.cache.ttl.products),
        },
    );
}

export async function getFaiOffersByCategory(categoryCodes: string[]): Promise<OffersByCategoryCode> {
    const offersByCategory = await getApiProductDetails(categoryCodes, true, false);
    const offersByCategoryCode: OffersByCategoryCode = {};

    offersByCategory.categories.forEach((cat) => {
        offersByCategoryCode[cat.code] = cat.products.reduce((uniqueProducts, product) => {
            const isInCategory = uniqueProducts.some(
                (p) =>
                    p.rangeNg === product.range_ng &&
                    p.commercialSeries === product.commercial_series &&
                    product.technology === p.technology,
            );
            if (!isInCategory) {
                uniqueProducts.push({
                    rangeNg: product.range_ng ?? 'xgbox',
                    commercialSeries: (product.commercial_series ?? product.classification)!,
                    technology: product.technology!,
                    shortDescription: product.short_description,
                    range: (product.range ?? product.classification)!,
                });
            }

            return uniqueProducts;
        }, [] as CategoryCodeInfo[]);
    });

    return offersByCategoryCode;
}
